import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Polygon, InfoWindow } from '@react-google-maps/api';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
	PlaceTag,
	PolygonGeometry,
	Coordinates,
} from 'interfaces/vector.interface';
import {
	setEditablePolygons,
	setSelectedPolygonIndex,
} from 'reducers/awdView.reducer';
import { PointData } from 'interfaces/maps.interface';

import { Colors, isLocalEnv } from 'utils/constants';
import titleCase from 'utils/titleCase';
import getPolygonVertexIndexClosestToCenter from 'utils/getPolygonVertexIndexClosestToCenter';
import extractValuesForPlaceTag from 'utils/extractValuesForPlaceTag';
import getDuration from 'utils/getDuration';

import useSeasonalConfigs from 'customHooks/useSeasonalConfigs';

type PolygonType = 'PRIMARY' | 'SECONDARY';

type PolygonUnitProps = {
	borderStoke: string;
	colorIndex: number;
	fillColor: string;
	isCursorOverPoint?: boolean;
	isInteractive?: boolean;
	optionalTooltipData?: any;
	parentPointSignatures?: string[];
	paths: PolygonGeometry;
	placeId?: string;
	polygonIndex: number;
	polygonMetaData: PlaceTag[];
	polygonType?: PolygonType;
	setIsPolygonMoving?: (a: boolean) => void;
	showEditLinkOnTooltip?: boolean;
	strokeWeight?: number;
};

const PolygonUnit: React.FC<PolygonUnitProps> = ({
	borderStoke,
	colorIndex,
	fillColor,
	isCursorOverPoint = false,
	isInteractive = false,
	optionalTooltipData,
	parentPointSignatures = [''],
	paths,
	placeId,
	polygonIndex,
	polygonMetaData,
	polygonType = 'SECONDARY',
	setIsPolygonMoving,
	showEditLinkOnTooltip = true,
	strokeWeight,
}): JSX.Element => {
	const polygonRef: any = useRef(null);
	const listenersRef: any = useRef([]);
	const prevVertexRef: any = useRef(null);
	const counterRef: any = useRef(0);
	const timerRef: any = useRef(null);
	const dispatch = useAppDispatch();
	const editablePolygons = useAppSelector((s) => s.awdView.editablePolygons);
	const selectedPolygonIndex = useAppSelector(
		(s) => s.awdView.selectedPolygonIndex,
	);
	const lat = useAppSelector((s) => s.router.location.query.lat);
	const lng = useAppSelector((s) => s.router.location.query.lng);
	const eventId = useAppSelector((s) => s.router.location.query.eventId);
	const pointData = useAppSelector((s) => s.awdView.pointMapTiles);
	const eventType = useAppSelector((s) => s.router.location.query.type);
	const debugMode =
		`${useAppSelector(
			(s) => s.router.location.query.debug,
		)}`.toLowerCase() === 'true';
	const [, seasonId, seasonKey] = useSeasonalConfigs();

	const [isEditable, setIsEditable] = useState(false);
	const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);
	const [toolTipAnchorIndex, setTooltipAnchorIndex] = useState(() => {
		let center = { lat: Number(lat), lng: Number(lng) };
		return getPolygonVertexIndexClosestToCenter(center, paths, 19);
	});

	const getPolygonOptions = useCallback(
		() => ({
			fillColor: fillColor || Colors[colorIndex % 11],
			fillOpacity: fillColor ? 1 : 0,
			strokeColor:
				borderStoke && borderStoke !== '' ? borderStoke : '#FADDFF',
			strokeOpacity: 1,
			strokeWeight: strokeWeight ?? isTooltipVisible ? 2 : 1.5,
			clickable: true,
			draggable: polygonType === 'PRIMARY',
			editable:
				isInteractive &&
				selectedPolygonIndex === polygonIndex &&
				isEditable,
			geodesic: false,
			// suppressUndo: false,
			zIndex: 2,
		}),
		[
			fillColor,
			colorIndex,
			borderStoke,
			isInteractive,
			polygonType,
			selectedPolygonIndex,
			polygonIndex,
			isEditable,
			isTooltipVisible,
			strokeWeight,
		],
	);

	const setInteraction = useCallback(
		(fn, params) => {
			if (!isInteractive) return undefined;
			return () => {
				fn(...params);
			};
		},
		[isInteractive],
	);

	const resetRefs = useCallback(() => {
		prevVertexRef.current = null;
		counterRef.current = 0;
		timerRef.current = null;
	}, []);

	const deleteVertexOnDoubleClick = useCallback(
		(
			currentPath: PolygonGeometry,
			mouseBtnClicked: number,
			vertexIndex: number,
		) => {
			clearTimeout(timerRef.current);
			// return if mouse btn other than left btn is clicked
			if (mouseBtnClicked > 1) {
				resetRefs();
				return;
			}
			timerRef.current = setTimeout(() => {
				counterRef.current = 0;
			}, 1000);
			if (vertexIndex !== prevVertexRef.current) {
				// vertex changed
				prevVertexRef.current = vertexIndex;
				counterRef.current = 1;
			} else {
				counterRef.current += 1;
				if (counterRef.current === 2) {
					clearTimeout(timerRef.current);
					resetRefs();
					currentPath.splice(vertexIndex, 1);
				}
			}
		},
		[resetRefs],
	);

	const onEdit = useCallback(
		(e) => {
			if (!isInteractive) return;
			if (
				polygonRef.current &&
				editablePolygons !== undefined &&
				Object.keys(editablePolygons).length > 0
			) {
				const polygons = editablePolygons;
				const nextPath = polygonRef.current
					.getPath()
					.getArray()
					.map((latLng: any) => {
						return { lat: latLng.lat(), lng: latLng.lng() };
					});

				if (e?.vertex !== undefined && nextPath.length > 3) {
					deleteVertexOnDoubleClick(
						nextPath,
						e.domEvent.which,
						e.vertex,
					);
				}

				polygons[`${polygonIndex}`].geometry = nextPath;
				dispatch(setEditablePolygons({ ...polygons }));
			}
		},
		[
			isInteractive,
			editablePolygons,
			polygonIndex,
			dispatch,
			deleteVertexOnDoubleClick,
		],
	);

	const handleClick = () => {
		if (!isInteractive) return;
		setIsEditable(!isEditable);
		dispatch(setSelectedPolygonIndex(polygonIndex));
	};

	const handleDragEvent = (
		e: google.maps.MapMouseEvent,
		dragStart: boolean,
	) => {
		if (!isInteractive) return;
		setIsPolygonMoving && setIsPolygonMoving(dragStart);
		onEdit(e);
	};

	const onUnmount = () => {
		listenersRef.current.forEach((lis: any) => lis.remove());
		polygonRef.current = null;
	};

	const onLoad = (polygon: google.maps.Polygon) => {
		polygonRef.current = polygon;
		const path = polygon.getPath();
		listenersRef.current.push(
			path.addListener('set_at', onEdit),
			path.addListener('insert_at', onEdit),
			path.addListener('remove_at', onEdit),
		);
	};

	const handleMouseOver = () => {
		setIsTooltipVisible(true);
	};

	const handleMouseOut = () => {
		setIsTooltipVisible(false);
	};

	useEffect(() => {
		if (![polygonIndex, undefined].includes(selectedPolygonIndex)) {
			setIsEditable(false);
		}
		return () => {
			resetRefs();
		};
	}, [polygonIndex, resetRefs, selectedPolygonIndex]);

	useEffect(() => {
		let center = { lat: Number(lat), lng: Number(lng) };
		setTooltipAnchorIndex(
			getPolygonVertexIndexClosestToCenter(center, paths, 19),
		);
	}, [lat, lng, paths]);

	return (
		<>
			<Polygon
				paths={paths}
				options={getPolygonOptions()}
				onClick={setInteraction(handleClick, [])}
				onMouseUp={(e) => onEdit(e)}
				onDrag={(e) => handleDragEvent(e, true)}
				onDragEnd={(e) => handleDragEvent(e, false)}
				onLoad={onLoad}
				onUnmount={onUnmount}
				onMouseOver={handleMouseOver}
				onMouseOut={handleMouseOut}
			/>
			{isTooltipVisible && !isCursorOverPoint && (
				<InfoWindow
					position={paths[toolTipAnchorIndex]}
					options={{ disableAutoPan: true, zIndex: 1 }}
				>
					<div>
						{getToolTipBody({
							eventId,
							showEditLinkOnTooltip,
							debugMode,
							optionalTooltipData,
							parentPointSignatures,
							placeTags: polygonMetaData,
							placeId,
							pointData,
							position: paths[toolTipAnchorIndex],
							seasonId,
							seasonKey,
							eventType,
						})}
					</div>
				</InfoWindow>
			)}
		</>
	);
};

export default React.memo(PolygonUnit);

const getToolTipBody = ({
	debugMode,
	eventId,
	showEditLinkOnTooltip = true,
	optionalTooltipData = [],
	parentPointSignatures,
	placeId = '',
	placeTags,
	pointData,
	position,
	seasonId,
	seasonKey,
	eventType,
}: {
	debugMode: boolean;
	eventId: string;
	showEditLinkOnTooltip?: boolean;
	optionalTooltipData?: {
		label: string;
		value: string[] | string;
		type: 'INFO' | 'URL';
	}[];
	parentPointSignatures: string | string[];
	placeId?: string;
	placeTags: PlaceTag[];
	pointData: Record<string, PointData>;
	position: Coordinates;
	seasonId: string;
	seasonKey: string;
	eventType?: string;
}) => {
	if (!!placeTags || optionalTooltipData.length > 0) {
		const tooltipData: {
			label: string;
			value: string[] | string;
			type: 'INFO' | 'URL';
		}[] = getTooltipData(placeTags, seasonKey);

		if (showEditLinkOnTooltip) {
			const link = createLink(
				position.lat,
				position.lng,
				placeTags,
				pointData,
				parentPointSignatures,
				seasonId,
				eventId,
				eventType,
			);

			if (link.length > 0) {
				tooltipData.push({
					label: 'Edit Polygon',
					value: link,
					type: 'URL',
				});
			}
		}

		if (isLocalEnv || debugMode) {
			tooltipData.push({
				label: 'placeId',
				value: `${placeId}`,
				type: 'INFO',
			});
		}

		return (
			<ul>
				{[...optionalTooltipData, ...tooltipData].map((el, idx) => {
					return (
						<li key={`${el.label}-${idx}`}>
							{titleCase(el.label)}:{' '}
							{el.type === 'URL' ? (
								<a
									href={
										Array.isArray(el.value)
											? el.value[0]
											: el.value
									}
									target="_blank"
								>
									Click here to redirect{' '}
									<OpenInNewIcon
										fontSize="inherit"
										sx={{ marginBottom: '-2px' }}
									/>
								</a>
							) : (
								`${el.value}`
							)}
						</li>
					);
				})}
			</ul>
		);
	}
	return null;
};

const getTooltipData = (placeTags: PlaceTag[], seasonKey: string) => {
	let res: {
		label: string;
		value: string[] | string;
		type: 'INFO' | 'URL';
	}[] = [];
	placeTags.forEach(
		({
			TagPropertyKey: tagKey,
			TagName: tagName,
			value: tagValue,
		}: PlaceTag) => {
			if (
				(tagName === 'PROGRAM' && tagKey === 'NAME') ||
				(tagName === 'TagName_FARMER' && tagKey === 'FARMER_ID') ||
				(tagName === 'PARENT' && tagKey === 'FARM_ID') ||
				(tagName === 'Actor' && tagKey === 'UGDN') ||
				(tagName === 'GEOFENCE' && tagKey === 'GEOFENCE_SOURCE')
			) {
				// TODO: handle for other program names as well
				if (
					tagName === 'PROGRAM' &&
					tagKey === 'NAME' &&
					(tagValue.stringValues.includes('AWD_22_KHARIF_PROGRAM') ||
						tagValue.stringValues.includes('AWD_22_RABI_PROGRAM'))
				) {
					res.push({
						label: 'PROGRAM_NAME',
						value:
							seasonKey === 'KHARIF_2022'
								? 'AWD_22_KHARIF'
								: seasonKey === 'RABI_2022'
								? 'AWD_22_Rabi'
								: seasonKey === 'KHARIF_2024'
								? 'KHARIF_2024'
								: seasonKey === 'RABI_2024'
								? 'AWD_24_Rabi'
								: seasonKey === 'KHARIF_2025'
								? 'KHARIF_2025'
								: seasonKey === 'RABI_2025'
								? 'AWD_25_Rabi'
								: seasonKey === 'KHARIF_2026'
								? 'KHARIF_2026'
								: 'KHARIF_2026',
						type: 'INFO',
					});
				} else {
					res.push({
						label: tagKey,
						value: extractValuesForPlaceTag(tagValue)[0],
						type: 'INFO',
					});
				}
				// if (tagName === 'GEOFENCE' && tagKey === 'GEOFENCE_SOURCE') {
				// 	res.push({
				// 		label: 'IsCopied',
				// 		value: `${extractValuesForPlaceTag(tagValue).includes(
				// 			'PREV_SEASON_REENGAGE_COPY',
				// 		)}`,
				// 		type: 'INFO',
				// 	});
				// }
			}
			if (
				(tagName === 'EARLY_STOP' && tagKey === 'DURATION_SECS') ||
				(tagName === 'EARLY_START' && tagKey === 'DURATION_SECS') ||
				(tagName === 'DELAYED_START' && tagKey === 'DURATION_SECS') ||
				(tagName === 'DELAYED_END' && tagKey === 'DURATION_SECS') ||
				(tagName === 'BOOKING_FLAG' && tagKey === 'INSIDE_BOOKING')
			) {
				if (tagName === 'EARLY_START' && tagKey === 'DURATION_SECS') {
					res.push({
						label: 'EARLY_START_BY',
						value: getDuration(
							extractValuesForPlaceTag(tagValue)[0],
						),
						type: 'INFO',
					});
				} else if (
					tagName === 'EARLY_STOP' &&
					tagKey === 'DURATION_SECS'
				) {
					res.push({
						label: 'EARLY_STOP_BY',
						value: getDuration(
							extractValuesForPlaceTag(tagValue)[0],
						),
						type: 'INFO',
					});
				} else if (
					tagName === 'DELAYED_START' &&
					tagKey === 'DURATION_SECS'
				) {
					res.push({
						label: 'DELAYED_START_BY',
						value: getDuration(
							extractValuesForPlaceTag(tagValue)[0],
						),
						type: 'INFO',
					});
				} else if (
					tagName === 'DELAYED_END' &&
					tagKey === 'DURATION_SECS'
				) {
					res.push({
						label: 'DELAYED_STOP_BY',
						value: getDuration(
							extractValuesForPlaceTag(tagValue)[0],
						),
						type: 'INFO',
					});
				} else {
					res.push({
						label: tagKey,
						value: extractValuesForPlaceTag(tagValue)[0],
						type: 'INFO',
					});
				}
			}
		},
	);
	return res;
};
const createLink = (
	newLat: number,
	newLng: number,
	placeTags: PlaceTag[],
	pointData: Record<string, PointData>,
	parentPointSignatures: string[] | string,
	seasonId: string,
	eventId: string,
	eventType?: string,
) => {
	let newFarmId;
	let newContentId;
	let link = '';
	placeTags.forEach(
		({
			TagPropertyKey: tagKey,
			TagName: tagName,
			value: tagValue,
		}: PlaceTag) => {
			if (tagName === 'PARENT' && tagKey === 'FARM_ID') {
				newFarmId = extractValuesForPlaceTag(tagValue);
			}
		},
	);
	if (
		parentPointSignatures !== undefined &&
		parentPointSignatures.length > 0 &&
		pointData !== undefined &&
		pointData[parentPointSignatures[0]] !== undefined
	) {
		newContentId =
			pointData[parentPointSignatures[0]]['placeTags'].find(
				(tag: PlaceTag) =>
					tag.TagName === 'CONTENT_METADATA' &&
					tag.TagPropertyKey === 'CONTENT_ID',
			)?.value.int64Values[0] ?? '';
	}
	const queryParams = [
		`lat=${newLat}`,
		`lng=${newLng}`,
		`farmId=${newFarmId}`,
		`eventId=${eventId}`,
		`contentId=${newContentId}`,
		`seasonId=${seasonId}`,
		eventType ? `type=${eventType}` : '',
	].filter((param) => param !== '');
	link = `${document.URL.split('?')[0]}?${queryParams.join('&')}`;
	return link;
};
